import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import type { IYoutube } from "../../../../pages/Party/YoutubeMatchingInfo";
import ConfirmBtn from "../../../Button/ConfirmBtn";
import RadioBtn from "../../../Button/RadioBtn";
import { PROVIDER_OPTIONS } from "./youtubeProvider";
import AlertModal from "../../../Modal/AlertModal";
import { saveYoutubeUrlAllocation } from "./youtubeExtrainfoApi";

export default function YoutubeProviderModal({ round, closeEvent, onSaved }: {
  round: IYoutube;
  closeEvent: () => void;
  onSaved: () => void;
}) {
  const [provider, setProvider] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const savingRef = useRef(false);

  const confirm = async () => {
    if (savingRef.current) return;
    const nextProvider = provider;
    if (!PROVIDER_OPTIONS.some((option) => option.value === nextProvider)) {
      setError("Provider를 선택해 주세요.");
      return;
    }
    if (nextProvider === round.provider) {
      setError("현재 Provider와 다른 Provider를 선택해 주세요.");
      return;
    }
    const roundNumber = Number(round.yt_round);
    const status = Number(round.yt_r_status);
    if (!round.partyid || !round.uid || !Number.isInteger(roundNumber) || roundNumber < 1 || roundNumber > 12 || ![1, 2, 3].includes(status)) {
      setError("현재 회차 정보를 확인할 수 없습니다. 목록을 새로고침해 주세요.");
      return;
    }
    setError("");
    if (!confirming) {
      setConfirming(true);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const result = await saveYoutubeUrlAllocation({
        provider: nextProvider,
        pid: String(round.partyid),
        uid: String(round.uid),
        email: round.yt_r_email ?? "",
        round: roundNumber,
        r_status: status,
        r_link: round.yt_r_link ?? "",
      });
      if (result.provider !== nextProvider || result.round !== roundNumber || result.r_status !== status) {
        throw new Error("Provider 저장 결과를 확인할 수 없습니다.");
      }
    } catch {
      setError("Provider 변경에 실패했습니다. 다시 시도해 주세요.");
      return;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
    toast.success("Provider가 변경되었습니다.");
    onSaved();
  };

  return (
    <AlertModal
      title={confirming ? "해당 Provider로 변경할까요?" : "Provider 변경"}
      desc={`${round.yt_round ?? "-"}회차`}
      pointDesc={
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", overflowWrap: "anywhere" }}>
          <div>현재 Provider: {round.provider || "-"}</div>
          {confirming ? (
            <div>변경 Provider: {provider}</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
              {PROVIDER_OPTIONS.map((option) => (
                <RadioBtn
                  key={option.value}
                  label={option.label}
                  check={provider === option.value}
                  clickEvent={() => {
                    setProvider(option.value);
                    setError("");
                  }}
                  noTooltip
                  sHeight={38}
                />
              ))}
            </div>
          )}
          {error && <span id="youtube-provider-error" role="alert">{error}</span>}
        </div>
      }
      customCancelBtn={<ConfirmBtn btnText="취소" btnColor="white" isDisabled={saving} btnClickEvent={closeEvent} />}
      customConfirmBtn={
        <ConfirmBtn btnText={saving ? "저장 중…" : "확인"} btnColor="mint" isDisabled={saving || !provider || provider === round.provider} btnClickEvent={() => void confirm()} />
      }
    />
  );
}
